/**
 * Morph Tracing — core initialization.
 *
 * Thin Morph layer over OpenLLMetry / Traceloop. `morphTracing()` initializes
 * Traceloop with a JSON OTLP exporter pointed at Morph's ingest endpoint
 * (`${baseUrl}/v1/traces`, `Authorization: Bearer <apiKey>`) and returns a
 * `MorphTracing` handle for interactions and tools.
 *
 * We deliberately override Traceloop's default protobuf exporter with the
 * JSON-over-HTTP exporter so the ingest service can parse plain OTLP/JSON.
 */
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import * as traceloop from '@traceloop/node-server-sdk';

import {
  createInteractionApi,
  createNoopInteraction,
  type Interaction,
  type Tracer,
} from './interaction.js';
import { metadata as buildMetadata } from './otel.js';
import type {
  EvalSelection,
  MetadataOptions,
  MorphTracingConfig,
  TraceContext,
} from './types.js';

const DEFAULT_BASE_URL = 'https://api.morphllm.com';

export interface ResolvedConfig {
  apiKey: string;
  baseUrl: string;
  appName: string;
  disableBatching: boolean;
  traceContent: boolean;
  headers?: Record<string, string>;
  debug: boolean;
  evals?: EvalSelection;
}

function log(debug: boolean, ...args: unknown[]): void {
  if (debug) console.log('[morph-tracing]', ...args);
}

/**
 * Handle returned by {@link morphTracing}. Create interactions with `begin()`,
 * get a non-interactive `tracer()`, and flush/shutdown the exporter.
 */
export class MorphTracing {
  private readonly cfg: ResolvedConfig;
  private readonly active = new Map<string, Interaction>();
  readonly enabled: boolean;

  constructor(config: MorphTracingConfig = {}) {
    const debug = config.debug ?? process.env.MORPH_TRACING_DEBUG === '1';
    const apiKey = config.apiKey ?? process.env.MORPH_API_KEY ?? '';
    const baseUrlRaw =
      config.baseUrl ?? process.env.MORPH_TRACES_URL ?? DEFAULT_BASE_URL;
    const baseUrl = baseUrlRaw.replace(/\/+$/, '');
    const isProd = process.env.NODE_ENV === 'production';

    this.cfg = {
      apiKey,
      baseUrl,
      appName: config.appName ?? process.env.npm_package_name ?? 'morph-app',
      disableBatching: config.disableBatching ?? !isProd,
      traceContent: config.traceContent ?? true,
      headers: config.headers,
      debug,
      evals: config.evals,
    };

    if (config.disabled) {
      log(debug, 'disabled — no tracing initialized');
      this.enabled = false;
      return;
    }
    if (!apiKey) {
      console.warn(
        '[morph-tracing] No API key (set MORPH_API_KEY or pass { apiKey }). Tracing disabled.',
      );
      this.enabled = false;
      return;
    }

    this.enabled = true;
    if (config.useExternalOtel) {
      // Customer drives their own NodeSDK; just register config, no SDK start.
      traceloop.initialize({
        baseUrl,
        apiKey,
        appName: this.cfg.appName,
        tracingEnabled: false,
        traceContent: this.cfg.traceContent,
        silenceInitializationMessage: true,
      });
      log(debug, 'external OTEL mode — add createSpanProcessor() to your NodeSDK');
      return;
    }

    const exporter = new OTLPTraceExporter({
      url: `${baseUrl}/v1/traces`,
      headers: { Authorization: `Bearer ${apiKey}`, ...this.cfg.headers },
    });

    traceloop.initialize({
      baseUrl,
      apiKey,
      appName: this.cfg.appName,
      exporter,
      disableBatch: this.cfg.disableBatching,
      traceContent: this.cfg.traceContent,
      instrumentModules: config.instrumentModules as NonNullable<
        Parameters<typeof traceloop.initialize>[0]
      >['instrumentModules'],
      tracingEnabled: true,
      traceloopSyncEnabled: false,
      silenceInitializationMessage: !debug,
    });
    log(
      debug,
      `initialized → ${baseUrl}/v1/traces (batching ${this.cfg.disableBatching ? 'off' : 'on'})`,
    );
  }

  /**
   * Begin a new traced interaction (a single user turn / agent run). On a
   * disabled instance this returns an inert no-op interaction — callbacks still
   * run, but no spans are created or shipped.
   */
  begin(ctx: TraceContext & { userId: string; event?: string }): Interaction {
    if (!this.enabled) return createNoopInteraction(ctx);
    // Per-interaction evals override the global default; omit both for none.
    const resolved = { ...ctx, evals: ctx.evals ?? this.cfg.evals };
    const interaction = createInteractionApi(resolved, this.cfg.traceContent, (eventId) =>
      this.active.delete(eventId),
    );
    this.active.set(interaction.getEventId()!, interaction);
    return interaction;
  }

  /** Look up an in-flight interaction by its eventId. */
  getActiveInteraction(eventId: string): Interaction | undefined {
    return this.active.get(eventId);
  }

  /**
   * Non-interactive tracer for batch jobs where you only care about
   * spans/token usage, not a user-facing interaction.
   */
  tracer(globalProperties: Record<string, string> = {}): Tracer {
    const ctx = { userId: globalProperties.userId ?? 'batch', properties: globalProperties };
    if (!this.enabled) return createNoopInteraction(ctx);
    return createInteractionApi(ctx, this.cfg.traceContent);
  }

  /** Build Vercel AI SDK telemetry metadata (see `@morphllm/morphsdk/tracing/otel`). */
  metadata(opts: MetadataOptions): Record<string, string> {
    return buildMetadata(opts);
  }

  /** Span processor for `useExternalOtel: true` integrations. */
  createSpanProcessor(
    options?: Parameters<typeof traceloop.createSpanProcessor>[0],
  ): ReturnType<typeof traceloop.createSpanProcessor> {
    // Traceloop's default exporter here is OTLP/protobuf, but Morph's ingest
    // parses OTLP/JSON only — supply the JSON exporter unless the caller
    // brings their own.
    const exporter =
      options?.exporter ??
      new OTLPTraceExporter({
        url: `${this.cfg.baseUrl}/v1/traces`,
        headers: { Authorization: `Bearer ${this.cfg.apiKey}`, ...this.cfg.headers },
      });
    return traceloop.createSpanProcessor({
      apiKey: this.cfg.apiKey,
      baseUrl: this.cfg.baseUrl,
      ...options,
      exporter,
    });
  }

  /** Flush any batched spans immediately. Safe to call when idle. */
  async forceFlush(): Promise<void> {
    if (!this.enabled) return;
    try {
      await traceloop.forceFlush();
    } catch (err) {
      log(this.cfg.debug, 'forceFlush error (ignored):', err);
    }
  }

  /** Flush and stop tracing. */
  async shutdown(): Promise<void> {
    await this.forceFlush();
  }
}

/**
 * Initialize Morph Tracing and auto-instrument supported AI SDKs.
 *
 * Requires the optional OpenTelemetry / Traceloop backends (see this module's
 * README). They install automatically with the SDK unless optional deps are
 * skipped; importing `/tracing` without them throws a module-not-found error
 * naming the missing package.
 */
export function morphTracing(config: MorphTracingConfig = {}): MorphTracing {
  return new MorphTracing(config);
}
